import React, { Fragment } from 'react';
import { translate } from 'translations/i18n';
import styled from 'styled-components';
import CCClipboard from 'components/shared/CCClipboard';
import { Steven } from 'helpers/steven';

const HiddenInput = styled.input`
  position: absolute;
  overflow: hidden;
  clip: rect(0 0 0 0);
  height: 1px;
  width: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
`;

type InstallationInstructionsProps = {
  showcasePublicIdentifier: string;
};

const InstallationInstructions = ({
  showcasePublicIdentifier,
}: InstallationInstructionsProps) => {
  const ROOT_SNIPPET = `<div\n data-showcase-id="${showcasePublicIdentifier}"\n id="companycam-showcase-root"\n></div>`;
  const SCRIPT_SNIPPET = `<script>\n (function () {\n  var ccShowcaseRoot = document.getElementById("companycam-showcase-root");\n  if (!ccShowcaseRoot.attachShadow || !window.fetch) { return; }\n  var ccShowcaseRootParent = document.getElementsByTagName("body")[0];\n  var ccShowcaseScript = document.createElement("script");\n  ccShowcaseScript.src = 'https://showcase.companycam.com/bundle.js';\n  ccShowcaseScript.type = "text/javascript";\n  ccShowcaseRootParent.appendChild(ccShowcaseScript);\n })();\n</script>`;

  const onScriptCopy = () => {
    Steven.elementClicked({
      id: 'copy_showcases_widget_script',
      content: 'showcases.setupGuide.buttonLabel',
      type: 'script_button',
      action: 'copy_showcase_widget_script_clicked',
    });
  };

  const onRootCopy = () => {
    Steven.elementClicked({
      id: 'copy_showcases_widget_root',
      content: 'showcases.setupGuide.buttonLabel',
      type: 'root_button',
      action: 'copy_showcase_widget_script_clicked',
    });
  };

  return (
    <Fragment>
      <h4 style={{ marginTop: 24 }}>
        {translate('showcases.setupGuide.stepLabel', { number: 1 })}
      </h4>
      <p className="instructions small" style={{ marginBottom: 8 }}>
        {translate('showcases.setupGuide.script.instructions')}
      </p>
      <div
        className="code-snippet-container"
        data-testid="showcases__installation-instructions-js-code-snippet"
        data-controller="clipboard"
        data-clipboard-success-text="Copied!"
      >
        <label>
          <pre>{SCRIPT_SNIPPET}</pre>
          <HiddenInput
            value={SCRIPT_SNIPPET}
            type="text"
            readOnly
            data-clipboard-target="source"
          />
        </label>
        <CCClipboard
          copy={SCRIPT_SNIPPET}
          className="ccb-medium-gray-tiny"
          onCopy={onScriptCopy}
        >
          <>
            <i className="mdi mdi-link-variant" />
            {translate('showcases.setupGuide.buttonLabel')}
          </>
        </CCClipboard>
      </div>
      <h4>{translate('showcases.setupGuide.stepLabel', { number: 2 })}</h4>
      <p className="instructions small" style={{ marginBottom: 8 }}>
        {translate('showcases.setupGuide.root.instructions')}
      </p>
      <div
        className="code-snippet-container"
        data-testid="showcases__installation-instructions-html-code-snippet"
        data-controller="clipboard"
        data-clipboard-success-text="Copied!"
      >
        <label>
          <pre>{ROOT_SNIPPET}</pre>
          <HiddenInput
            value={ROOT_SNIPPET}
            type="text"
            readOnly
            data-clipboard-target="source"
          />
        </label>
        <CCClipboard
          copy={ROOT_SNIPPET}
          className="ccb-medium-gray-tiny"
          onCopy={onRootCopy}
        >
          <>
            <i className="mdi mdi-link-variant" />
            {translate('showcases.setupGuide.buttonLabel')}
          </>
        </CCClipboard>
      </div>

      <p className="instructions small" style={{ marginBottom: 8 }}>
        {translate('showcases.setupGuide.help.instructions1')}
        <a
          className="inline-link"
          target="_blank"
          rel="noopener noreferrer"
          href="https://help.companycam.com/en/articles/6828410-create-and-publish-a-showcase-for-your-website"
          data-testid="showcases__setup-guide-link"
        >
          {translate('showcases.setupGuide.help.instructions2')}
        </a>
        {translate('showcases.setupGuide.help.instructions3')}
      </p>
    </Fragment>
  );
};

export default InstallationInstructions;
